/**
 * @author William A. Livesley
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-10-25
 * @description Implementation of the CookieConsent component with category settings.
 * @filename cookie-consent.tsx
 */
import React from "react";
import { Router } from "../../utilities/router";
import {
  CookieConsentPreferences,
  acceptAllPreferences,
  createDefaultPreferences,
  getCookieConsentPreferences,
  hasCookieConsentDecision,
  rejectAllPreferences,
  setCookieConsentPreferences,
  syncCookieConsentDocumentAttribute,
} from "../../utilities/cookie-consent-storage";
import { initializeTracking } from "../../utilities/tracking-consent";
import { consentStore } from "../../utilities/gtag-consent-store";

interface CookieCategory {
  id: "essential" | "analytics" | "marketing";
  title: string;
  description: string;
  alwaysOn?: boolean;
}

const COOKIE_CATEGORIES: CookieCategory[] = [
  {
    id: "essential",
    title: "Essential cookies",
    description: "These cookies are needed for our website to function and be secure.",
    alwaysOn: true,
  },
  {
    id: "analytics",
    title: "Performance cookies",
    description:
      "These cookies help us understand how you use our website and improve your experience.",
  },
  {
    id: "marketing",
    title: "Marketing cookies",
    description:
      "These cookies help us and our trusted partners to serve personalized ads and measure their performance.",
  },
];

/**
 * The state of the CookieConsent component
 *
 * @interface CookieConsentState
 */
export interface CookieConsentState {
  hideConsent: boolean;
  showSettings: boolean;
  expandedCategory: "essential" | "analytics" | "marketing" | null;
  draftPreferences: CookieConsentPreferences;
}

/**
 * A React component that renders a cookie consent notice and settings modal
 *
 * @export
 * @class CookieConsent
 * @extends {React.Component<{}, CookieConsentState>}
 */
export class CookieConsent extends React.Component<{}, CookieConsentState> {
  private choiceSaved = false;

  /**
   * Constructor
   */
  constructor(props: {}) {
    super(props);
    this.state = {
      hideConsent: hasCookieConsentDecision() || !consentStore.requiresBanner(),
      showSettings: false,
      expandedCategory: "essential",
      draftPreferences: createDefaultPreferences(false, false),
    };
  }

  componentDidMount(): void {
    syncCookieConsentDocumentAttribute();

    // requiresBanner() at construction may reflect the pre-initialize default (true) if
    // region detection hasn't resolved yet - re-check once it has and hide the banner if
    // the real region-based policy turns out not to require it.
    consentStore.ready.then(({ requiresBanner }) => {
      if (!requiresBanner && !this.state.hideConsent && !hasCookieConsentDecision()) {
        this.setState({ hideConsent: true });
      }
    });
  }

  /**
   * Persists preferences and updates tracking consent.
   */
  savePreferences = (preferences: CookieConsentPreferences) => {
    if (this.choiceSaved || getCookieConsentPreferences() !== null) {
      return;
    }

    this.choiceSaved = true;
    setCookieConsentPreferences(preferences);
    initializeTracking(preferences);

    this.setState({
      hideConsent: true,
      showSettings: false,
      draftPreferences: preferences,
    });
  };

  openSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      showSettings: true,
      expandedCategory: "essential",
      draftPreferences: createDefaultPreferences(false, false),
    });
  };

  closeSettings = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.setState({
      showSettings: false,
    });
  };

  toggleCategory = (category: "analytics" | "marketing") => {
    this.setState((prevState) => ({
      draftPreferences: {
        ...prevState.draftPreferences,
        [category]: !prevState.draftPreferences[category],
      },
    }));
  };

  toggleExpanded = (category: CookieCategory["id"]) => {
    this.setState((prevState) => ({
      expandedCategory: prevState.expandedCategory === category ? null : category,
    }));
  };

  goToCookiePolicy = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    Router.to("legal", ["section=cookies-section"], "privacy-policy");
  };

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    if ((this.state.hideConsent || getCookieConsentPreferences() !== null) && !this.state.showSettings) {
      return null;
    }

    return (
      <>
        {!this.state.hideConsent && getCookieConsentPreferences() === null ? (
          <div className="cookie-consent-container" role="dialog" aria-live="polite" aria-label="Cookie consent">
            <div className="cookie-consent-content">
              <p>
                We use cookies and similar technologies to improve your experience, analyze site traffic, and support
                marketing. You can accept all, reject all, or manage your cookie preferences.
              </p>
              <a href="/legal?section=cookies-section#:privacy-policy" onClick={this.goToCookiePolicy}>
                Cookie policy
              </a>
              <div className="cookie-consent-actions">
                <button
                  type="button"
                  className="cookie-consent-btn cookie-consent-btn-reject"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.savePreferences(rejectAllPreferences());
                  }}
                >
                  Reject all
                </button>
                <button
                  type="button"
                  className="cookie-consent-btn cookie-consent-btn-settings"
                  onClick={this.openSettings}
                >
                  Cookie settings
                </button>
                <button
                  type="button"
                  className="cookie-consent-btn cookie-consent-btn-accept"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.savePreferences(acceptAllPreferences());
                  }}
                >
                  Accept all
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {this.state.showSettings ? this.renderSettingsModal() : null}
      </>
    );
  }

  private renderSettingsModal(): React.ReactNode {
    const { draftPreferences, expandedCategory } = this.state;

    return (
      <div
        className="cookie-settings-overlay"
        role="presentation"
        onClick={this.closeSettings}
      >
        <div
          className="cookie-settings-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie settings"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="cookie-settings-header">
            <h2>Cookie settings</h2>
            <button
              type="button"
              className="cookie-settings-close"
              aria-label="Close cookie settings"
              onClick={this.closeSettings}
            >
              ×
            </button>
          </div>

          <p className="cookie-settings-intro">
            Change your cookie preferences for each category of cookies. To find out more, read our{" "}
            <a href="/legal?section=cookies-section#:privacy-policy" onClick={this.goToCookiePolicy}>
              Cookie policy
            </a>
            .
          </p>

          <ul className="cookie-settings-categories">
            {COOKIE_CATEGORIES.map((category) => {
              const isExpanded = expandedCategory === category.id;
              const enabled =
                category.id === "essential"
                  ? true
                  : draftPreferences[category.id as "analytics" | "marketing"];

              return (
                <li key={category.id} className="cookie-settings-category">
                  <div className="cookie-settings-category-row">
                    <button
                      type="button"
                      className="cookie-settings-category-toggle"
                      aria-expanded={isExpanded}
                      onClick={() => this.toggleExpanded(category.id)}
                    >
                      <span className={`cookie-settings-chevron${isExpanded ? " is-expanded" : ""}`} aria-hidden="true">
                        ›
                      </span>
                      <span className="cookie-settings-category-title">{category.title}</span>
                    </button>

                    {category.alwaysOn ? (
                      <span className="cookie-settings-always-on">Always on</span>
                    ) : (
                      <button
                        type="button"
                        className={`cookie-settings-switch${enabled ? " is-on" : ""}`}
                        role="switch"
                        aria-checked={enabled}
                        aria-label={`${category.title} ${enabled ? "on" : "off"}`}
                        onClick={() => this.toggleCategory(category.id as "analytics" | "marketing")}
                      >
                        <span className="cookie-settings-switch-thumb" />
                      </button>
                    )}
                  </div>

                  {isExpanded ? (
                    <p className="cookie-settings-category-description">{category.description}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>

          <div className="cookie-settings-actions">
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn-accept"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.savePreferences(draftPreferences);
              }}
            >
              Confirm
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn-settings"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.savePreferences(acceptAllPreferences());
              }}
            >
              Accept all
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn-reject"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.savePreferences(rejectAllPreferences());
              }}
            >
              Reject all
            </button>
          </div>
        </div>
      </div>
    );
  }
}
